import { officialInvoiceSlugToNumber } from "./officialInvoiceCustomerRoute.ts";

export const FORKSEEK_CONTINUATION_FALLBACK = "/seek";
export const FORKSEEK_PAID_PLAN_KEYS = ["forkseek_plus", "forkseek_pro"] as const;

export type ForkSeekPaidPlanKey = typeof FORKSEEK_PAID_PLAN_KEYS[number];

export type ForkSeekContinuationPurpose =
  | "password_login"
  | "seek_signup"
  | "seek_verification"
  | "seek_resend"
  | "password_recovery"
  | "oauth_completion"
  | "paid_plan";

export type CanonicalForkSeekContinuation = {
  raw: string;
  canonical: string;
  pathname: string;
  portal: "seek";
  purpose: ForkSeekContinuationPurpose;
  queryKeys: string[];
  fragment: null;
};

const SENTINEL_ORIGIN = "https://forkseek-continuation.invalid";
const MAX_CONTINUATION_LENGTH = 1_024;
const AMBIGUOUS_PATH_ENCODING = /%(?:25)*(?:2e|2f|5c)/iu;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const PAYMENT_ID = /^[A-Za-z0-9._~-]{1,128}$/u;
const PAYMENT_NUMBER = /^\d{1,12}(?:\.\d{1,2})?$/u;
const PAYMENT_MESSAGE = /^[A-Za-z0-9 ._~-]{1,160}$/u;
const PAYMENT_STATUS = new Set(["paid", "failed"]);

function hasControlOrWhitespace(value: string) {
  return [...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x20 || (codePoint >= 0x7f && codePoint <= 0x9f);
  });
}

const SENSITIVE_OR_NESTED_KEYS = new Set([
  "access_token",
  "refresh_token",
  "token",
  "token_hash",
  "type",
  "code",
  "state",
  "error",
  "error_code",
  "error_description",
  "provider_token",
  "provider_refresh_token",
  "expires_in",
  "expires_at",
  "token_type",
  "oauth_state",
  "oauth_attempt",
  "code_verifier",
  "pkce_verifier",
  "password",
  "credential",
  "authorization",
  "api_key",
  "returnto",
  "redirectto",
  "next",
  "continue",
  "callback",
]);

const STATIC_SEEK_PATHS = new Set([
  "/seek",
  "/seek/onboarding",
  "/seek/dashboard",
  "/seek/profile",
  "/seek/cv",
  "/seek/jobs",
  "/seek/saved-jobs",
  "/seek/applications",
  "/seek/notifications",
  "/seek/settings",
  "/seek/settings/subscription",
  "/seek/settings/credits",
  "/seek/settings/invoices",
  "/seek/interview-practice",
  "/seek/account",
]);

function canonicalPathShape(raw: string, parsed: URL): boolean {
  if (parsed.origin !== SENTINEL_ORIGIN || parsed.username || parsed.password || parsed.port) return false;
  if (raw.includes("#") || raw.includes("\\") || parsed.hash) return false;

  const queryIndex = raw.indexOf("?");
  const rawPath = queryIndex === -1 ? raw : raw.slice(0, queryIndex);
  if (!rawPath || rawPath.startsWith("//") || rawPath.includes("//")) return false;
  if (AMBIGUOUS_PATH_ENCODING.test(rawPath)) return false;
  if (rawPath.split("/").some((segment) => segment === "." || segment === "..")) return false;
  return parsed.pathname === rawPath;
}

function isProtectedSeekPath(pathname: string): boolean {
  if (STATIC_SEEK_PATHS.has(pathname)) return true;
  const applicationId = /^\/seek\/applications\/([^/]+)$/u.exec(pathname)?.[1];
  if (applicationId && UUID.test(applicationId)) return true;
  const practiceId = /^\/seek\/interview-practice\/([^/]+)$/u.exec(pathname)?.[1];
  if (practiceId && UUID.test(practiceId)) return true;

  const invoiceSlug = /^\/invoices\/([A-Za-z0-9-]{1,128})$/u.exec(pathname)?.[1];
  return Boolean(invoiceSlug && officialInvoiceSlugToNumber(invoiceSlug)?.startsWith("FSB2C/"));
}

export function normalizeForkSeekPaidPlanKey(value: unknown): ForkSeekPaidPlanKey | null {
  if (typeof value !== "string") return null;
  return (FORKSEEK_PAID_PLAN_KEYS as readonly string[]).includes(value)
    ? value as ForkSeekPaidPlanKey
    : null;
}

function decodedQueryEntries(url: URL): Array<[string, string]> | null {
  const raw = url.search.startsWith("?") ? url.search.slice(1) : url.search;
  if (!raw) return [];

  const parsedEntries = Array.from(url.searchParams.entries());
  const rawEntries = raw.split("&");
  if (rawEntries.length !== parsedEntries.length) return null;

  const seen = new Set<string>();
  for (let index = 0; index < rawEntries.length; index += 1) {
    const rawName = rawEntries[index].split("=", 1)[0];
    const [name] = parsedEntries[index];
    if (!rawName || rawName !== name || !/^[a-z_]+$/u.test(name) || seen.has(name)) return null;
    if (SENSITIVE_OR_NESTED_KEYS.has(name.toLowerCase())) return null;
    seen.add(name);
  }

  return parsedEntries;
}

function canonicalQuery(
  url: URL,
  purpose: ForkSeekContinuationPurpose,
): { search: string; keys: string[] } | null {
  const entries = decodedQueryEntries(url);
  if (!entries) return null;
  if (entries.length === 0) return { search: "", keys: [] };

  if (url.pathname === "/seek/onboarding") {
    if (purpose !== "password_login" && purpose !== "oauth_completion") return null;
    if (entries.length !== 1 || entries[0][0] !== "plan") return null;
    const plan = normalizeForkSeekPaidPlanKey(entries[0][1]);
    return plan ? { search: `?plan=${encodeURIComponent(plan)}`, keys: ["plan"] } : null;
  }

  if (url.pathname === "/seek/settings/subscription") {
    if (entries.length === 1 && entries[0][0] === "plan") {
      const plan = normalizeForkSeekPaidPlanKey(entries[0][1]);
      return plan ? { search: `?plan=${encodeURIComponent(plan)}`, keys: ["plan"] } : null;
    }
    if (entries.length === 1 && entries[0][0] === "recurring_authorization_attempt_id") {
      if (purpose !== "password_login") return null;
      const value = entries[0][1];
      return UUID.test(value)
        ? { search: `?recurring_authorization_attempt_id=${encodeURIComponent(value)}`, keys: [entries[0][0]] }
        : null;
    }
  }

  if (url.pathname === "/seek/jobs") {
    if (purpose !== "password_login" && purpose !== "oauth_completion") return null;
    if (entries.length !== 1 || entries[0][0] !== "highlight" || !UUID.test(entries[0][1])) return null;
    return { search: `?highlight=${encodeURIComponent(entries[0][1])}`, keys: ["highlight"] };
  }

  if (url.pathname === "/seek/settings/subscription" || url.pathname === "/seek/settings/credits") {
    if (purpose !== "password_login") return null;
    const allowed = new Set(["id", "transaction_id", "status", "message", "amount", "fee", "currency"]);
    if (!entries.some(([key]) => key === "id") || entries.some(([key]) => !allowed.has(key))) return null;
    for (const [key, value] of entries) {
      if (key === "id" && !PAYMENT_ID.test(value)) return null;
      if (key === "transaction_id" && !UUID.test(value)) return null;
      if (key === "status" && !PAYMENT_STATUS.has(value)) return null;
      if ((key === "amount" || key === "fee") && !PAYMENT_NUMBER.test(value)) return null;
      if (key === "currency" && value !== "SAR") return null;
      if (key === "message" && !PAYMENT_MESSAGE.test(value)) return null;
    }
    const params = new URLSearchParams();
    for (const [key, value] of entries) params.append(key, value);
    return { search: `?${params.toString()}`, keys: entries.map(([key]) => key) };
  }

  return null;
}

export function validateForkSeekContinuation(
  value: unknown,
  purpose: ForkSeekContinuationPurpose,
): CanonicalForkSeekContinuation | null {
  if (typeof value !== "string") return null;
  const raw = value;
  if (
    !raw ||
    raw.length > MAX_CONTINUATION_LENGTH ||
    raw !== raw.trim() ||
    !raw.startsWith("/") ||
    raw.startsWith("//") ||
    hasControlOrWhitespace(raw)
  ) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw, SENTINEL_ORIGIN);
  } catch {
    return null;
  }
  if (!canonicalPathShape(raw, parsed)) return null;

  if (purpose === "password_recovery") return null;
  if (purpose === "paid_plan" && parsed.pathname !== "/seek/settings/subscription") return null;
  if (!isProtectedSeekPath(parsed.pathname)) return null;

  const query = canonicalQuery(parsed, purpose);
  if (!query) return null;
  if (purpose === "paid_plan" && query.keys.join(",") !== "plan") return null;

  return {
    raw,
    canonical: `${parsed.pathname}${query.search}`,
    pathname: parsed.pathname,
    portal: "seek",
    purpose,
    queryKeys: query.keys,
    fragment: null,
  };
}

export function sanitizeForkSeekContinuation(
  value: unknown,
  purpose: ForkSeekContinuationPurpose,
): string | null {
  return validateForkSeekContinuation(value, purpose)?.canonical ?? null;
}
